import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  OnDestroy,
  output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  combineLatest,
  filter,
  map,
  Observable,
  of,
  ReplaySubject,
  take,
  takeUntil,
} from 'rxjs';
import { BdAbstractMultiSelect } from './abstract-multi-select';
import { BdAbstractOption, BdUiMultiSelectConfig } from './multi-select.config';
import { IftaLabel } from 'primeng/iftalabel';
import { ReactiveFormsModule } from '@angular/forms';
import { MultiSelectModule } from 'primeng/multiselect';
import { Message } from 'primeng/message';
import { AsyncPipe } from '@angular/common';
import { OverlayOptions } from 'primeng/api';
import { BdDataTestDirective } from '@bindoc-projects/util-lib/ui';

@Component({
  selector: 'bd-ui-multi-select',
  template: `
    <p-iftalabel>
      <!-- PrimeNG 22 treats an unset selectAll as "not all selected", so toggle-all could never deselect -->
      <p-multiselect
        [selectAll]="null"
        [inputId]="selectConfig.id"
        [data-test]="selectConfig.id"
        [formControl]="selectCtrl"
        [options]="allOptions | async"
        fluid
        variant="filled"
        [class]="
          valid
            ? 'mb-4'
            : errorMessage
              ? 'ng-invalid ng-dirty'
              : 'mb-4 ng-invalid ng-dirty'
        "
        [loading]="searching"
        [showClear]="true"
        dataKey="dataKey"
        optionLabel="displayName"
        [filter]="showFilter"
        [filterFields]="['searchTerm']"
        filterBy="searchTerm"
        [filterPlaceHolder]="placeholderLabel"
        [emptyFilterMessage]="noEntriesFoundLabel"
        [emptyMessage]="noEntriesFoundLabel"
        [overlayOptions]="overlayOptions"
        [maxSelectedLabels]="maxSelectedLabels()"
        [selectedItemsLabel]="selectedItemsLabel"
        [showToggleAll]="showFilter"
        [showHeader]="showFilter"
      >
      </p-multiselect>
      <label [for]="selectConfig.id">{{ displayLabel }}</label>
    </p-iftalabel>
    @if (errorMessage) {
      <p-message
        severity="error"
        variant="simple"
        size="small"
        icon="pi pi-exclamation-circle"
        [id]="selectConfig.id + '-help'"
      >
        {{ errorMessage }}
      </p-message>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    IftaLabel,
    MultiSelectModule,
    ReactiveFormsModule,
    AsyncPipe,
    BdDataTestDirective,
    Message,
  ],
})
export class BdUiMultiSelectComponent<T>
  extends BdAbstractMultiSelect<T, BdAbstractOption<T>>
  implements OnDestroy
{
  readonly config = input<BdUiMultiSelectConfig<T>>();
  readonly initialSelection = input<Array<T> | Observable<Array<T>>>([]);
  readonly selectOptions = input<Observable<T[]>>(of([]));
  /**
   * How many selected items the trigger lists by name before collapsing to
   * "{n} <label> ausgewählt".
   *
   * Raise it where the values are short and the set small enough to read at a
   * glance — a list of years says more than its own count does.
   */
  readonly maxSelectedLabels = input<number>(3);

  readonly selection = output<Array<T>>();
  readonly optionsChange = output<Array<T>>();

  public readonly overlayOptions: OverlayOptions = {
    mode: 'overlay',
    appendTo: 'body',
  };

  public showFilter = false;
  private readonly _cdr = inject(ChangeDetectorRef);
  private readonly _destroyRef = inject(DestroyRef);
  private static readonly FILTER_THRESHOLD = 3;

  public selectConfig: Required<BdUiMultiSelectConfig<T>> = {
    id: 'multi-select-option-group',
    selectLabel: 'Element',
    option: {
      uniqueFnc: () => '',
      sortFnc: () => 0,
      searchOptionAccessor: () => '',
      searchOptionDisplayName: () => '',
    },
    emitEventOnInitialSelection: false,
    valid$: new ReplaySubject<{
      isValid: boolean;
      errorMessage?: string;
    }>(1),
  };

  public get displayLabel(): string {
    const label = this.selectConfig?.selectLabel;
    if (!label) return '';
    return typeof label === 'string' ? label : label['=1'];
  }

  // Event subjects for coordinating initialization
  private _configReady$ = new ReplaySubject<void>(1);
  private _initialSelection$ = new ReplaySubject<Array<T>>(1);
  private _optionsLoaded$ = new ReplaySubject<void>(1);

  constructor() {
    super();

    // Initialize component when config and options are ready (does not wait for initial selection)
    combineLatest([this._configReady$, this._optionsLoaded$])
      .pipe(take(1), takeUntil(this._onDestroy))
      .subscribe(() => {
        this.init(this.selectConfig);
        this._cdr.markForCheck();
      });

    // Apply initial selection once a non-empty array arrives AND config + options are ready.
    combineLatest([
      this._configReady$,
      this._optionsLoaded$,
      this._initialSelection$.pipe(
        filter((v) => v.length > 0),
        take(1),
        map((initialValues) =>
          initialValues.map((it) => this.transformToWrapper(it)),
        ),
      ),
    ])
      .pipe(take(1), takeUntil(this._onDestroy))
      .subscribe(([, , wrappedSelection]) => {
        this.setSelection(wrappedSelection, {
          emitEvent: this.selectConfig.emitEventOnInitialSelection,
        });
        this._updateSelectedItemsLabel(wrappedSelection.length);
        this._cdr.markForCheck();
      });

    this.allOptions.pipe(takeUntil(this._onDestroy)).subscribe((options) => {
      this.showFilter =
        options.length > BdUiMultiSelectComponent.FILTER_THRESHOLD;
      this._cdr.markForCheck();
    });

    // React to config changes
    effect(() => {
      const config = this.config();
      if (!config) return;
      const configLabel =
        typeof config.selectLabel === 'string'
          ? config.selectLabel
          : config.selectLabel['=1'];
      this.selectConfig = {
        id: `multi-select-option-group-${configLabel}`,
        emitEventOnInitialSelection: false,
        valid$: new ReplaySubject<{
          isValid: boolean;
          errorMessage?: string;
        }>(1),
        ...config,
        option: {
          searchOptionDisplayName: (option) =>
            config.option.searchOptionAccessor(option),
          ...config.option,
        },
      };
      this._configReady$.next();
    });

    // React to initialSelection changes
    effect(() => {
      const value = this.initialSelection();
      if (value instanceof Observable) {
        value
          .pipe(takeUntilDestroyed(this._destroyRef))
          .subscribe((it) => this._initialSelection$.next(it));
      } else {
        this._initialSelection$.next(value ?? []);
      }
    });

    // React to selectOptions changes
    effect(() => {
      const options$ = this.selectOptions();
      this._loadOptions(options$);
    });
  }

  ngOnDestroy(): void {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  protected emptySelection(): BdAbstractOption<T>[] {
    return new Array<BdAbstractOption<T>>();
  }

  protected onSelectionChange(selection: BdAbstractOption<T>[]): void {
    this.selection.emit(selection.map((it) => it.realValue));
  }

  protected transformToWrapper(option: T): BdAbstractOption<T> {
    return {
      realValue: option,
      dataKey: this.selectConfig.option.uniqueFnc(option),
      searchTerm: this.selectConfig.option.searchOptionAccessor(option),
      displayName: this.selectConfig.option.searchOptionDisplayName(option),
    };
  }

  private _loadOptions(options$: Observable<T[]>): void {
    this.searching = true;

    options$
      .pipe(
        map((options) => options.map((it) => this.transformToWrapper(it))),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe({
        next: (availableOptions) => {
          this.allOptions.next(
            availableOptions
              .slice()
              .sort((a, b) =>
                this.selectConfig.option.sortFnc(a.realValue, b.realValue),
              ),
          );
          this.searching = false;
          this.optionsChange.emit(availableOptions.map((it) => it.realValue));
          this._optionsLoaded$.next();
        },
        error: (err) => {
          console.warn(
            `Laden von ${this.selectConfig.selectLabel} nicht möglich`,
            err,
          );
          this.allOptions.next([]);
          this.searching = false;
          this.valid = false;
          this.errorMessage = 'Laden fehlgeschlagen';
          this._optionsLoaded$.next();
          this._cdr.markForCheck();
        },
      });
  }
}
